// cleanup_convert_to_typescript_file_ROUTES

import React, { lazy } from 'react';

import CheckFree from 'components/checkfree/CheckFreeRouteHandler';
import { WORKFLOWS, WORKSPACES } from 'constants.js';

// Components
const ManageTransferImportMaps = lazy(() =>
  import(
    /* webpackChunkName: 'ManageTransferImportMaps' */ 'components/importManager/ManageTransferImportMaps'
  )
);
const ViewTransferFileImport = lazy(() =>
  import(
    /* webpackChunkName: 'ViewTransferFileImport' */ 'components/importManager/ViewFileImport'
  )
);
const ACHPosPayWorkspace = lazy(() =>
  import(
    /* webpackChunkName: 'ACHPosPayWorkspace' */ 'components/achPosPayWorkspace/ACHPosPayWorkspace'
  )
);
const PendingDecisionsAlert = lazy(() =>
  import(
    /* webpackChunkName: 'PendingDecisionsAlert' */ 'components/pendingDecisionsForm/PendingDecisionsAlert'
  )
);
const AddContact = lazy(() =>
  import(/* webpackChunkName: 'AddContact' */ 'components/addContact/AddContact')
);
const Alerts = lazy(() => import(/* webpackChunkName: 'Alerts' */ 'components/alerts/Alerts'));
const AssignAlertsWorkflow = lazy(() =>
  import(
    /* webpackChunkName: 'AssignAlertsWorkflow' */ 'components/assignAlerts/AssignAlertsWorkflow'
  )
);
const BalanceAndTransactions = lazy(() =>
  import(
    /* webpackChunkName: 'BalanceAndTransactions' */ 'components/balanceAndTransactions/workspace/BalanceAndTransactions'
  )
);
const Bulletins = lazy(() =>
  import(/* webpackChunkName: 'Bulletins' */ 'components/bulletins/Bulletins')
);
const CashFlowForecasting = lazy(() =>
  import(
    /* webpackChunkName: 'CashFlowForecasting' */ 'components/cashFlowForecasting/CashFlowForecasting'
  )
);
const UpdateSecurityQuestions = lazy(() =>
  import(
    /* webpackChunkName: 'UpdateSecurityQuestions' */ 'components/updateSecurityQuestions/UpdateSecurityQuestions'
  )
);

const ContactCenterUser = lazy(() =>
  import(
    /* webpackChunkName: 'ContactCenterUser' */ 'components/contactCenterUser/ContactCenterUser'
  )
);
const ContactCenterWorkspace = lazy(() =>
  import(
    /* webpackChunkName: 'ContactCenterWorkspace' */ 'components/contactCenterWorkspace/ContactCenterWorkspace'
  )
);
const ContactCenterApprovePayee = lazy(() =>
  import(
    /* webpackChunkName: 'ContactCenterApprovePayee' */ 'components/contactCenterApprovePayee/ContactCenterApprovePayee'
  )
);

const CreateTransfers = lazy(() =>
  import(
    /* webpackChunkName: 'CreateTransfers' */ 'components/createTransfers/CreateTransfersWrapper'
  )
);
const Dashboard = lazy(() =>
  import(/* webpackChunkName: 'Dashboard' */ 'components/dashboard/Dashboard')
);
const DepositCheckView = lazy(() =>
  import(
    /* webpackChunkName: 'DepositCheckView' */ 'components/mobileRemoteDepositCheckCapture/DepositCheckView'
  )
);
const DepositHistoryView = lazy(() =>
  import(
    /* webpackChunkName: 'DepositHistoryView' */ 'components/mobileRemoteDepositCheckCapture/DepositHistory'
  )
);
const DepositDetailView = lazy(() =>
  import(
    /* webpackChunkName: 'DepositDetailView' */ 'components/mobileRemoteDepositCheckCapture/DepositDetail'
  )
);
const ElectronicReportsItemView = lazy(() =>
  import(
    /* webpackChunkName: 'ElectronicReportsItemView' */ 'components/electronicReportsItemView/ElectronicReportsItemView'
  )
);
const FailedImportDetailsWrapper = lazy(() =>
  import(
    /* webpackChunkName: 'FailedImportDetailsWrapper' */ 'components/failedImportDetails/FailedImportDetailsWrapper'
  )
);
const HelpCenterWorkspace = lazy(() =>
  import(/* webpackChunkName: 'HelpCenterWorkspace' */ 'components/helpCenter/HelpCenterWorkspace')
);
const ImportBatchDetails = lazy(() =>
  import(
    /* webpackChunkName: 'ImportBatchDetails' */ 'components/importBatchDetails/ImportBatchDetails'
  )
);
const ImportIssueDetailsWrapper = lazy(() =>
  import(
    /* webpackChunkName: 'ImportIssueDetailsWrapper' */ 'components/importIssueDetails/ImportIssueDetailsWrapper'
  )
);
const IssuesVoids = lazy(() =>
  import(/* webpackChunkName: 'IssuesVoids' */ 'components/issuesVoids/IssuesVoids')
);
const ModifySimplePayment = lazy(() =>
  import(
    /* webpackChunkName: 'ModifySimplePayment' */ 'components/modifySimplePayment/ModifySimplePayment'
  )
);
const NewUserPage = lazy(() =>
  import(/* webpackChunkName: 'NewUserPage' */ 'components/newUser/NewUserWrapper')
);
const PaymentTemplateConfirmation = lazy(() =>
  import(
    /* webpackChunkName: 'PaymentTemplateConfirmation' */ 'components/widgets/paymentTemplates/PaymentTemplateConfirmation'
  )
);
const PaymentTemplateInitiate = lazy(() =>
  import(
    /* webpackChunkName: 'PaymentTemplateInitiate' */ 'components/widgets/paymentTemplates/PaymentTemplateInitiate/PaymentTemplateInitiate'
  )
);

const PaymentTemplateInitiateMultipleACH = lazy(() =>
  import(
    /* webpackChunkName: 'PaymentTemplateInitiate' */ 'components/paymentTemplates/paymentTemplateInitiateMultipleACH/InitiateMultipleACH.tsx'
  )
);

const PaymentTemplateInitiateMultipleACHConfirmation = lazy(() =>
  import(
    /* webpackChunkName: 'PaymentTemplateInitiate' */ 'components/paymentTemplates/paymentTemplateInitiateMultipleACH/MultiAchConfirmation.tsx'
  )
);

const PaymentTemplateInitiateReview = lazy(() =>
  import(
    /* webpackChunkName: 'PaymentTemplateInitiateReview' */ 'components/widgets/paymentTemplates/PaymentTemplateInitiate/PaymentTemplateInitiateReview'
  )
);
const PaymentTemplateItemView = lazy(() =>
  import(
    /* webpackChunkName: 'PaymentTemplateItemView' */ 'components/widgets/paymentTemplates/PaymentTemplateItemView'
  )
);
const PaymentRuleModifications = lazy(() =>
  import(
    /* webpackChunkName: 'PaymentRuleModifications' */ 'components/paymentRuleModifications/PaymentRuleModifications'
  )
);
const PaymentRule = lazy(() =>
  import(/* webpackChunkName: 'PaymentRule' */ 'components/paymentRule/PaymentRule')
);
const PositivePay = lazy(() =>
  import(/* webpackChunkName: 'PositivePay' */ 'components/positivePay/PositivePay')
);
const PositivePayItemDetail = lazy(() =>
  import(
    /* webpackChunkName: 'PositivePayItemDetail' */ 'components/positivePayItemDetail/PositivePayItemDetail'
  )
);
const ProfileManagement = lazy(() =>
  import(
    /* webpackChunkName: 'ProfileManagement' */ 'components/profileManagement/ProfileManagement'
  )
);
const ResetPasswordPage = lazy(() =>
  import(/* webpackChunkName: 'ResetPasswordPage' */ 'components/resetPassword/ResetPasswordPage')
);
const ReviewTransfer = lazy(() =>
  import(/* webpackChunkName: 'ReviewTransfer' */ 'components/reviewTransfer/ReviewTransfer')
);
const SendPayment = lazy(() =>
  import(/* webpackChunkName: 'SendPayment' */ 'components/sendPayment/SendPaymentFormProvider')
);
const SsoWorkflow = lazy(() =>
  import(/* webpackChunkName: 'SsoWorkflow' */ 'components/ssoWorkflow/SsoWorkflow')
);
const SsoError = lazy(() =>
  import(/* webpackChunkName: 'SsoError' */ 'components/ssoError/SsoError')
);
const Statements = lazy(() =>
  import(/* webpackChunkName: 'Statements' */ 'components/statements/Statements')
);
const StopPaymentRequestsWrapper = lazy(() =>
  import(
    /* webpackChunkName: 'StopPaymentRequestsWrapper' */ 'components/stopPaymentRequests/StopPaymentRequestsWrapper'
  )
);
const StopPaymentsWorkspace = lazy(() =>
  import(
    /* webpackChunkName: 'StopPaymentsWorkspace' */ 'components/stopPaymentsWorkspace/StopPaymentsWorkspace'
  )
);
const TransactionDetails = lazy(() =>
  import(
    /* webpackChunkName: 'TransactionDetails' */ 'components/transactionDetails/TransactionDetails'
  )
);
const TransferActivity = lazy(() =>
  import(/* webpackChunkName: 'TransferActivity' */ 'components/transferActivity/TransferActivity')
);
const TransferWorkspace = lazy(() =>
  import(
    /* webpackChunkName: 'TransferWorkspace' */ 'components/transferWorkspace/TransferWorkspace'
  )
);
const UserMaintenance = lazy(() =>
  import(
    /* webpackChunkName: 'UserMaintenance' */ 'components/widgets/userMaintenance/UserMaintenance'
  )
);
const UserPendingChanges = lazy(() =>
  import(
    /* webpackChunkName: 'UserPendingChanges' */ 'components/userPendingChanges/UserPendingChanges'
  )
);
const ViewAlertWorkflow = lazy(() =>
  import(/* webpackChunkName: 'ViewAlertWorkflow' */ 'components/alertsList/ViewAlertWorkflow')
);
const ViewCriteria = lazy(() =>
  import(/* webpackChunkName: 'ViewCriteria' */ 'components/viewCriteria/ViewCriteria')
);
const ViewFileDetails = lazy(() =>
  import(/* webpackChunkName: 'ViewFileDetails' */ 'components/viewFileDetails/ViewFileDetails')
);
const ViewFileWarnings = lazy(() =>
  import(/* webpackChunkName: 'ViewFileWarnings' */ 'components/viewFileWarnings/ViewFileWarnings')
);
const ViewStopPayments = lazy(() =>
  import(/* webpackChunkName: 'ViewStopPayments' */ 'components/viewStopPayments/ViewStopPayments')
);
const ViewTemplate = lazy(() =>
  import(/* webpackChunkName: 'ViewTemplate' */ 'components/viewTemplate/ViewTemplate')
);
const ViewTransfers = lazy(() =>
  import(/* webpackChunkName: 'ViewTransfers' */ 'components/viewTransfers/ViewTransfers')
);
const ViewWireFileSummary = lazy(() =>
  import(
    /* webpackChunkName: 'ViewWireFileSummary' */ 'components/viewWireFileSummary/ViewWireFileSummary'
  )
);
const WireTransferImportActionSwitcher = lazy(() =>
  import(
    /* webpackChunkName: 'WireTransferImportActionSwitcher' */ 'components/importManager/TransferImportActionSwitcher'
  )
);
const LoanPaymentContainer = lazy(() =>
  import(/* webpackChunkName: 'LoanPaymentContainer' */ 'components/loanPayment/LoanPayment')
);

const InitiateLoanPaymentContainer = lazy(() =>
  import(
    /* webpackChunkName: 'InitiateLoanPayment' */ 'components/loanPayment/initiatePaymentFromTemplate/InitiateLoanPaymentContainer'
  )
);

const PrintACH = lazy(() => import(/* webpackChunkName: 'PrintACH' */ 'components/ach/PrintACH'));

const PrintCDReports = lazy(() =>
  import(
    /* webpackChunkName: 'PrintCDReports' */ 'components/widgets/controlledDisbursementsWidget/PrintCDReports'
  )
);

const SMBLoanTemplatesRouteHandler = lazy(() =>
  import(
    /* webpackChunkName: 'SMBLoanTemplatesRouteHandler' */ 'components/widgets/smallBusinessPayments/loanTemplates/SMBLoanTemplatesRouteHandler'
  )
);

const PrintWire = lazy(() =>
  import(/* webpackChunkName: 'PrintWire' */ 'components/wires/PrintWire')
);

/**
 * Route object, we are going to use in our routing configuration thing..
 * @typedef {Object} Route
 * @property {Component} render - allows inline rendering without undesired remounting.
 * @property {Component} component - a component that needs to be rendered
 * @property {boolean} exact - needs an exact match in location.pathname
 */

/**
 * Routes across all the application
 *
 * We can also instead, just pass a directly a component we want to use
 * in such case `component` property from `Route` is used.
 *
 * @returns {Object<Route>}
 */

export const wrapProps = (Component, addProps) => (props) =>
  (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Component {...props} {...addProps} />
  );

export default {
  // TODO: Some kind of grouping here, would be nice
  // to locate atleast the main sections (BTR, PAYMENTS etc)
  [`${WORKFLOWS.SB_LOAN_PAYMENT}/initiate/template/:templateId`]: InitiateLoanPaymentContainer,
  [`${WORKFLOWS.LOAN_PAYMENT}/:action/:type/:id?`]: LoanPaymentContainer,
  [WORKFLOWS.APPROVE_PAYEE]: ContactCenterApprovePayee,
  [WORKSPACES.ALERT]: Alerts,
  [WORKSPACES.ACHPOSPAY]: ACHPosPayWorkspace,
  [WORKFLOWS.PRINT_CD_REPORTS]: { component: PrintCDReports, exact: true },
  [`${WORKSPACES.BTR}/:accounts?/:id?`]: BalanceAndTransactions,
  [WORKSPACES.BULLETINS]: Bulletins,
  [WORKSPACES.CFF]: CashFlowForecasting,
  [WORKSPACES.CHANGEPASSWORD]: ResetPasswordPage,
  [WORKFLOWS.CREATE_ISSUES_VOIDS]: IssuesVoids,
  [WORKFLOWS.CREATE_PAYMENT_RULE]: PaymentRule,
  [WORKFLOWS.CONFIRM_PAYMENT_RULE]: PaymentRule,
  [`${WORKFLOWS.MODIFY_PAYMENT_RULE}/:id`]: PaymentRule,
  [`${WORKFLOWS.COPY_PAYMENT_RULE}/:id`]: PaymentRule,
  [`${WORKFLOWS.VIEW_PAYMENT_RULE}/:id`]: PaymentRule,
  [`${WORKFLOWS.PAY_AND_ADD_RULE}/:transactionId`]: PendingDecisionsAlert,
  [`${WORKFLOWS.RETURN_AND_ADD_RULE}/:transactionId`]: PendingDecisionsAlert,
  [`${WORKFLOWS.VIEW_PAYMENT_RULE_MODIFICATIONS}/:ruleId`]: PaymentRuleModifications,
  [`${WORKFLOWS.COLLECT_PAYMENT}/:action/:paymentType/:traceNum`]: ModifySimplePayment,
  [`${WORKFLOWS.COLLECT_PAYMENT}/:paymentType/:traceNum`]: ModifySimplePayment,
  [WORKFLOWS.COLLECT_PAYMENT]: SendPayment,
  [WORKSPACES.DASHBOARD]: Dashboard,
  [`${WORKFLOWS.DUAL_CONTROL_PAYMENT_RULE}/:id`]: PaymentRule,
  [`${WORKSPACES.FRAUD}/:exceptionId`]: PositivePayItemDetail,
  [WORKSPACES.FRAUD]: PositivePay,
  [`${WORKSPACES.MODIFYUSER}/:id`]: NewUserPage,
  [WORKSPACES.NEWUSER]: {
    component: NewUserPage,
    exact: true
  },
  [`${WORKSPACES.PYMTTPL}/confirmation`]: PaymentTemplateConfirmation,
  [`${WORKSPACES.PYMTTPL}/initiate/review`]: PaymentTemplateInitiateReview,
  [`${WORKSPACES.PYMTTPL}/initiate`]: PaymentTemplateInitiate,
  [`${WORKSPACES.PYMTTPL}/initiateMultipleACH/confirmation`]:
    PaymentTemplateInitiateMultipleACHConfirmation,
  [`${WORKSPACES.PYMTTPL}/initiateMultipleACH`]: PaymentTemplateInitiateMultipleACH,

  [`${WORKSPACES.TRANSFER}/viewTransferFileImport/:id`]: {
    component: ViewTransferFileImport,
    exact: true
  },
  [`${WORKSPACES.PYMTTPL}/:paymentCategory/:companyId/:templateCode`]: PaymentTemplateItemView,
  [`${WORKSPACES.PYMTTPL}/:paymentCategory/:templateCode`]: PaymentTemplateItemView,
  [`${WORKSPACES.TRANSFER}/manageTransferImportMaps`]: {
    component: ManageTransferImportMaps,
    exact: true
  },
  [`${WORKSPACES.TRANSFER}/manageTransferImportMaps/:action(\\w+)?/:id(\\d+)?`]:
    WireTransferImportActionSwitcher,
  [WORKSPACES.PROFILEMGMT]: ProfileManagement,
  '/report/:reportId': ElectronicReportsItemView,
  [WORKFLOWS.REVIEW_TRANSFER]: ReviewTransfer,
  '/security-questions': () => <UpdateSecurityQuestions page />,
  [`${WORKFLOWS.SEND_PAYMENT}/:action/:paymentType/:traceNum`]: ModifySimplePayment,
  [`${WORKFLOWS.SEND_PAYMENT}/:paymentType/:traceNum`]: ModifySimplePayment,
  [WORKFLOWS.SEND_PAYMENT]: SendPayment,
  '/sso-login/:id': SsoWorkflow,
  '/sso-error': SsoError,
  [WORKSPACES.ESTATEMENT]: Statements,
  [WORKSPACES.STOPPAYMENT]: {
    component: StopPaymentsWorkspace,
    exact: true
  },
  [`${WORKSPACES.STOPPAYMENT}/:id`]: ViewStopPayments,
  '/stop-payments-request': StopPaymentRequestsWrapper,
  '/transaction-details': {
    component: TransactionDetails,
    exact: true
  },
  '/transfers-workspace': {
    component: TransferWorkspace,
    exact: true
  },
  '/transfers-workspace/transfer-activity': TransferActivity,
  '/transfers-workspace/:id': ViewTransfers,
  [WORKSPACES.CBEADMIN]: UserMaintenance,
  '/view-file-details': ViewFileDetails,
  '/view-file-warnings/:id': ViewFileWarnings,
  [`${WORKFLOWS.ADD_CONTACT}/:id?`]: AddContact,
  [WORKFLOWS.ASSIGN_ALERTS]: AssignAlertsWorkflow,
  [`${WORKFLOWS.MODIFY_ALERT}/:id`]: AssignAlertsWorkflow,
  [`${WORKFLOWS.VIEW_ALERT}/:id`]: ViewAlertWorkflow,
  [WORKSPACES.CONTACTCNTER]: {
    component: ContactCenterWorkspace,
    exact: true
  },
  '/help-center': {
    component: HelpCenterWorkspace
  },
  // [`${WORKFLOWS.CONTACT_CENTER}/:id`]: ContactCenter,
  [`${WORKFLOWS.CONTACT_CENTER_USER}/:id`]: ContactCenterUser,
  [`${WORKFLOWS.CONTACT_CENTER_USER}/:id/approve`]: ContactCenterUser,
  '/import-batch-details/:batchSequence/:pendingNumber': ImportBatchDetails,
  [WORKFLOWS.CREATE_TRANSFERS]: CreateTransfers,
  [WORKFLOWS.CREATE_TEMPLATE]: CreateTransfers,
  [WORKFLOWS.INITIATE_TRANSFER]: CreateTransfers,
  [WORKFLOWS.MODIFY_TEMPLATE]: CreateTransfers,
  [`${WORKFLOWS.USER_PENDING_CHANGES}/:id`]: UserPendingChanges,
  [`${WORKFLOWS.VIEW_IMPORT_ISSUE}/:id`]: ImportIssueDetailsWrapper,
  [`${WORKFLOWS.VIEW_FAILED_FILE}/:id`]: FailedImportDetailsWrapper,
  [`${WORKFLOWS.VIEW_TEMPLATE}/:id`]: ViewTemplate,
  [WORKFLOWS.VIEW_CRITERIA]: ViewCriteria,
  [`${WORKFLOWS.VIEW_WIRE_FILE_SUMMARY}/:map/:id(\\d+)`]: ViewWireFileSummary,
  [WORKSPACES.DEPOSITCHCK]: DepositCheckView,
  [WORKSPACES.DEPOSITHIST]: DepositHistoryView,
  [`${WORKSPACES.DEPOSITDETAIL}/:batchRef`]: DepositDetailView,
  [WORKSPACES.DEPOSITHIST]: DepositHistoryView,
  [WORKFLOWS.PRINT_ACH_PAYMENT]: PrintACH,
  [WORKFLOWS.PRINT_WIRE_PAYMENT]: PrintWire,
  [`${WORKSPACES.CHECKFREE}/:module?/:workflowMode?/:userCode?`]: CheckFree,
  [`${WORKFLOWS.SB_LOAN_TEMPLATE}/:action/:type/:id?`]: SMBLoanTemplatesRouteHandler
};
